import type { Rect } from './surfaces';

export const MAX_DISCOVERED = 12;
export const MIN_SURFACE_HEIGHT = 24;
/** A surface has to be something the companion can stand on top of. Past this
 *  fraction of the world's height an element is a layout container — the page
 *  wrapper, a full-height panel — and accepting it would both put the
 *  companion on the page's top edge and, since a container encloses its
 *  children, hide every real card inside it. */
export const MAX_SURFACE_HEIGHT_FRACTION = 0.5;
/** Backstop only. Invalidation is driven by layout change; this bounds how
 *  long a stale scan can survive if something moves without an observer
 *  noticing — a font swap, a late image, a transform that reflows nothing. */
export const DISCOVERY_TTL_MS = 30_000;

/** How many elements may be measured per beat. getBoundingClientRect is cheap
 *  relative to a style read, and the budget has to clear the app's chrome —
 *  a sidebar alone runs to a hundred-plus elements before any content. Also
 *  the ceiling on rect reads for ranking: every geometrically-qualifying
 *  candidate needs its rect read before the smallest-first sort can happen. */
export const MAX_RECT_READS = 400;

/** How many elements may have their computed style read per beat. This is the
 *  layout-forcing call, so it is paid only for candidates already the right
 *  shape to be a surface. */
const MAX_STYLE_READS = MAX_DISCOVERED * 3;

/** How many elements may be hit-tested per beat. elementFromPoint forces
 *  layout like the style read does, so it is paid only for candidates that
 *  already passed every cheap filter — visibility included. */
const MAX_HIT_TESTS = MAX_DISCOVERED * 3;

interface DiscoverInput {
  root: Element;
  petWidth: number;
  now: number;
  exclude?: Element | null;
}

interface CacheEntry {
  root: Element;
  petWidth: number;
  exclude: Element | null;
  expiresAt: number;
  generation: number;
  result: Rect[];
}

let cache: CacheEntry | null = null;
let generation = 0;

/**
 * Mark the discovered geometry stale.
 *
 * Scanning on a timer meant paying for a full sweep twice a minute on a page
 * that had not moved, while still standing on stale rects for up to that long
 * after one that had. Callers observing real layout change call this instead:
 * a quiet page never rescans, and a reflow is picked up on the next beat.
 */
export function invalidateDiscovery(): void {
  generation += 1;
}

export function resetDiscoveryCache(): void {
  cache = null;
}

function rectOf(el: Element): Rect | null {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

interface StyleInfo {
  visible: boolean;
  /** Draws a background, border or shadow — i.e. is furniture, not a wrapper. */
  paints: boolean;
  borderRadius: number;
}

/**
 * Whether the element draws anything a character could stand on.
 *
 * Size alone accepts far too much: measured in the running app, a hero banner,
 * four tab-bar wrappers sharing one rect, a fade gradient and a text span all
 * qualified, and the companion slept on the banner 1100px above the floor.
 * Furniture paints — a background, a border, or a shadow. Layout primitives
 * and decoration do not.
 */
function paintsSurface(style: CSSStyleDeclaration): boolean {
  const bg = style.backgroundColor;
  const opaque = bg !== '' && bg !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(bg);
  return opaque
    || parseFloat(style.borderTopWidth) > 0
    || (style.boxShadow !== '' && style.boxShadow !== 'none');
}

function readStyle(el: Element): StyleInfo {
  const style = window.getComputedStyle(el);
  const visible = style.visibility !== 'hidden' && style.display !== 'none';
  const paints = paintsSurface(style);
  // Only a plain pixel value is trustworthy without a per-corner layout
  // computation this module doesn't have; anything else (%, em, mixed
  // per-corner shorthand) is left unhandled rather than guessed at.
  const parsed = /^([\d.]+)px$/.exec(style.borderRadius);
  return { visible, paints, borderRadius: parsed ? Number(parsed[1]) : 0 };
}

function insetForRadius(rect: Rect, radius: number): Rect {
  if (radius <= 0) return rect;
  const width = rect.right - rect.left;
  const cap = Math.min(radius, width / 2 - 1);
  if (cap <= 0) return rect;
  return { ...rect, left: rect.left + cap, right: rect.right - cap };
}

function isExcluded(el: Element, exclude: Element | null | undefined): boolean {
  return !!exclude && (el === exclude || exclude.contains(el));
}

/**
 * Is `el` the top-most thing at the centre of its own top edge? Rejects a
 * surface another element visually covers, so the companion never stands on
 * something the user cannot see.
 *
 * jsdom has no layout engine, so `elementFromPoint` always returns null
 * there — treated as "not occluded" and accepted, or every test in this
 * file (and the whole feature under jsdom) would find no surfaces at all.
 */
function isOccluded(el: Element, rect: Rect): boolean {
  if (typeof document.elementFromPoint !== 'function') return false;
  const x = (rect.left + rect.right) / 2;
  const y = rect.top + 2;
  const hit = document.elementFromPoint(x, y);
  if (!hit) return false;
  return hit !== el && !el.contains(hit);
}

function contains(outer: Rect, inner: Rect): boolean {
  return inner.left >= outer.left && inner.right <= outer.right
    && inner.top >= outer.top && inner.bottom <= outer.bottom;
}

/**
 * True when `rect` conflicts with something already accepted — either it is
 * enclosed by an accepted rect, or (since candidates are processed smallest
 * area first) it encloses one. Checking only the first direction is what let
 * DOM order decide: a large container processed before its children used to
 * win by simply coming first, regardless of which one was the real surface.
 */
function nestedInAccepted(rect: Rect, accepted: Rect[]): boolean {
  return accepted.some((a) => contains(a, rect) || contains(rect, a));
}

export function discoverSurfaces({ root, petWidth, now, exclude }: DiscoverInput): Rect[] {
  // petWidth and exclude both shape the result — petWidth sets the minimum
  // walkable width, and exclude drops the companion's own layer — so both
  // belong in the key. The scale slider changes petWidth while the page is
  // otherwise still, which no generation bump would catch.
  if (cache
    && cache.root === root
    && cache.petWidth === petWidth
    && cache.exclude === (exclude ?? null)
    && cache.generation === generation
    && now < cache.expiresAt) {
    return cache.result;
  }

  const minWidth = petWidth * 2;
  const rootRect = rectOf(root);
  const maxHeight = rootRect
    ? (rootRect.bottom - rootRect.top) * MAX_SURFACE_HEIGHT_FRACTION
    : Infinity;
  const candidates = root.querySelectorAll('*');
  const qualifying: { el: Element; rect: Rect }[] = [];
  let inspected = 0;

  for (const el of candidates) {
    // One budget here: a rect read is cheap enough to afford across the whole
    // subtree, and it has to be — app chrome contributes hundreds of small
    // elements before the first real surface, so a tight cap would stop the
    // scan inside the sidebar and never reach the content.
    if (inspected >= MAX_RECT_READS) break;
    if (isExcluded(el, exclude)) continue;
    if (el.hasAttribute('data-companion-surface')) continue;
    if (el.getAttribute('data-slot')?.startsWith('companion-')) continue;

    inspected += 1;
    const rect = rectOf(el);
    if (!rect) continue;

    const width = rect.right - rect.left;
    const height = rect.bottom - rect.top;
    if (width < minWidth || height < MIN_SURFACE_HEIGHT) continue;
    if (height > maxHeight) continue;

    qualifying.push({ el, rect });
  }

  // Smallest area first, so an inner card claims its space before its
  // enclosing container can swallow it — acceptance below is first-come.
  qualifying.sort((a, b) => (
    (a.rect.right - a.rect.left) * (a.rect.bottom - a.rect.top)
    - (b.rect.right - b.rect.left) * (b.rect.bottom - b.rect.top)
  ));

  const accepted: Rect[] = [];
  let styled = 0;
  let hitTested = 0;

  for (const { el, rect } of qualifying) {
    if (styled >= MAX_STYLE_READS) break;
    if (nestedInAccepted(rect, accepted)) continue;

    // Only now, on the few candidates that are actually surface-shaped, pay
    // for the layout-forcing style read.
    styled += 1;
    const info = readStyle(el);
    if (!info.visible) continue;
    if (!info.paints) continue;

    if (hitTested >= MAX_HIT_TESTS) continue;
    hitTested += 1;
    if (isOccluded(el, rect)) continue;

    accepted.push(insetForRadius(rect, info.borderRadius));
  }

  const result = accepted
    .sort((a, b) => (
      (b.right - b.left) * (b.bottom - b.top) - (a.right - a.left) * (a.bottom - a.top)
    ))
    .slice(0, MAX_DISCOVERED);

  cache = {
    root,
    petWidth,
    exclude: exclude ?? null,
    expiresAt: now + DISCOVERY_TTL_MS,
    generation,
    result,
  };
  return result;
}
